import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CustomerDTO, PageCustomerDTO } from '@/shared/api/model';
import { useEssenceSearchCustomers } from '../api/essence-customer-api';

const { Text } = Typography;

interface AttachExistingMemberModalProps {
  visible: boolean;
  branch: string;
  groupCustNo: string;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (payload: { memberBranch?: string; memberCustNo: string; isLeader?: boolean }) => void;
}

const AttachExistingMemberModal: React.FC<AttachExistingMemberModalProps> = ({
  visible,
  branch,
  groupCustNo,
  loading,
  onCancel,
  onSubmit,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | null>(null);
  const [isLeader, setIsLeader] = useState(false);
  const [results, setResults] = useState<PageCustomerDTO | null>(null);

  const searchMutation = useEssenceSearchCustomers({
    mutation: {
      onSuccess: (data) => {
        setResults(data);
      },
      onError: () => {
        void message.error('Failed to search individual customers');
      },
    },
  });

  const runSearch = (term = searchTerm) => {
    searchMutation.mutate({
      data: {
        branch,
        custType: ['IND'],
        searchTerm: term || undefined,
        page: 0,
        size: 20,
      },
    });
  };

  useEffect(() => {
    if (!visible) {
      setSearchTerm('');
      setSelectedCustomer(null);
      setIsLeader(false);
      setResults(null);
      return;
    }

    runSearch('');
  }, [visible]);

  const columns: ColumnsType<CustomerDTO> = useMemo(() => [
    {
      title: 'Select',
      key: 'select',
      width: 70,
      render: (_, record) => (
        <Radio
          checked={selectedCustomer?.branch === record.branch && selectedCustomer?.custNo === record.custNo}
          onChange={() => setSelectedCustomer(record)}
        />
      ),
    },
    {
      title: 'Customer No',
      key: 'custNo',
      render: (_, record) => `${record.branch}-${record.custNo}`,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'custType',
      key: 'custType',
      render: (value?: string) => <Tag color="blue">{value || 'IND'}</Tag>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (value?: string) => value || '-',
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      render: (value?: string) => value || '-',
    },
  ], [selectedCustomer]);

  const handleSubmit = () => {
    if (!selectedCustomer?.custNo) {
      void message.warning('Please select an individual customer to convert');
      return;
    }

    onSubmit({
      memberBranch: selectedCustomer.branch,
      memberCustNo: selectedCustomer.custNo,
      isLeader,
    });
  };

  const dataSource = (results?.content || []).filter(
    (customer) => !(customer.branch === branch && customer.custNo === groupCustNo),
  );

  return (
    <Modal
      title="Add Existing Individual to Group"
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
          Convert to Member
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message="Choose an existing individual customer to convert into a group member."
        />

        <Input.Search
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onSearch={(value) => runSearch(value)}
          placeholder="Search individual by name, phone, mobile, or ID"
          enterButton
          allowClear
          loading={searchMutation.isPending}
        />

        <Radio.Group value={isLeader ? 'leader' : 'member'} onChange={(event) => setIsLeader(event.target.value === 'leader')}>
          <Space>
            <Radio value="member">Regular member</Radio>
            <Radio value="leader">Group leader</Radio>
          </Space>
        </Radio.Group>

        {selectedCustomer ? (
          <Text type="secondary">
            Selected: {selectedCustomer.branch}-{selectedCustomer.custNo} {selectedCustomer.name ? `- ${selectedCustomer.name}` : ''}
          </Text>
        ) : null}

        <Table
          rowKey={(record) => `${record.branch}-${record.custNo}`}
          columns={columns}
          dataSource={dataSource}
          loading={searchMutation.isPending}
          pagination={{
            current: results ? (results.number ?? 0) + 1 : 1,
            pageSize: results?.size || 20,
            total: results?.totalElements || 0,
            showSizeChanger: false,
            onChange: (page, pageSize) => {
              searchMutation.mutate({
                data: {
                  branch,
                  custType: ['IND'],
                  searchTerm: searchTerm || undefined,
                  page: page - 1,
                  size: pageSize,
                },
              });
            },
          }}
          scroll={{ x: 760 }}
        />
      </Space>
    </Modal>
  );
};

export default AttachExistingMemberModal;
